/**
 * @vaiu/utils - Shared utility functions
 * 
 * This package contains utility functions used across all Vaiu apps.
 */

export { cn } from './cn';
export {
  persistLabsEnabled,
  clearLabsEnabled,
  isLabsEnabledThisSession,
  isLabsUnlocked,
} from './labsSession';
export * from './currency';
export * from './mmMoney';
export * from './i18n';
export { digitalWalletTranslations } from './digitalWalletI18n';
export { pickProfileBalanceForCurrency } from './profileBalance';
export {
  getVaiuCrossSubdomainCookieDomain,
  readCrossSubdomainAuthCookie,
  setCrossSubdomainAuthCookie,
  clearCrossSubdomainAuthCookie,
  resolveSharedAuthToken,
  normalizeBearerAuthToken,
} from './crossDomainAuth';
export {
  buyerHostnameToMerchantHostname,
  merchantHostnameToBuyerHostname,
  buyerOriginFromMerchantHostname,
  merchantOriginFromBuyerHostname,
  sharedAuthCookieParentDomain,
} from './hostPairing';
export {
  OTP_SECURITY,
  normalizeOtpIdentifier,
  isValidOtpIdentifier,
  maskOtpIdentifier,
  formatLockoutDuration,
  formatCountdown,
  getOtpVerifyGuardState,
  recordOtpVerifyFailure,
  clearOtpVerifyGuard,
  getResendCooldownRemaining,
  markOtpSent,
  applyServerLockout,
  type OtpChannel,
  type OtpVerifyGuardState,
} from './otpLoginSecurity';
export {
  buildFcmDeviceInfo,
  fetchVapidPublicKey,
  registerVapidSubscription,
  unregisterVapidSubscription,
  fetchVapidPushStatus,
  sendVapidTestNotification,
  registerFcmToken,
  unregisterFcmToken,
  fetchFcmStatus,
  sendFcmTestNotification,
  getPushAuthHeaders,
  type VapidSubscriptionPayload,
  type VapidPushStatus,
  type FcmStatus,
  type FcmDeviceInfo,
  type FcmSubscribeResult,
  type PushApiOptions,
} from './pushApi';
export {
  isUnfundedTokenStatus,
  paypalApprovalUrl,
  isFramedWindow,
  goToPayPal,
  toAbsolutePayPalUrl,
  resolvePayPalCallbackBaseUrl,
  buildPayPalCallbackUrl,
  createPayPalFundingSessionId,
  openPayPalFundingTab,
  navigatePayPalFundingTab,
  closePayPalFundingTab,
  publishPayPalFundingResult,
  subscribePayPalFundingResult,
  isPayPalFundingReturnTab,
  readPayPalSessionId,
  VAIU_PAYPAL_TAB_NAME,
  VAIU_PAYPAL_CHANNEL,
  PAYPAL_SESSION_PARAM,
  PAYPAL_TAB_PARAM,
  type PayPalFundingBroadcast,
} from './paypalFunding';
export {
  isBankTransferOutChannelEnabled,
  isPayPalPayoutDestinationReady,
  isPaypalTransferOutChannelEnabled,
  isStripeConnectTransferOutAvailable,
  mergeTransferOutPreferencePayloadWithProfile,
  type ProfileTransferOutShape,
  type TransferOutPreferencePayload,
  type TransferOutPaypalFields,
} from './transferOut/paypalPayout';

/**
 * Robust clipboard utility with fallback methods
 * @param text - Text to copy to clipboard
 * @param successCallback - Optional callback for successful copy
 * @param errorCallback - Optional callback for failed copy
 */
export async function copyToClipboard(
  text: string,
  successCallback?: (text: string) => void,
  errorCallback?: (error: Error, text: string) => void
): Promise<boolean> {
  try {
    // Primary method: modern clipboard API
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      successCallback?.(text);
      return true;
    }
    
    // Fallback method: execCommand (for older browsers)
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    
    if (successful) {
      successCallback?.(text);
      return true;
    } else {
      throw new Error('Copy command failed');
    }
  } catch (err) {
    console.error('Failed to copy to clipboard:', err);
    const error = err instanceof Error ? err : new Error('Unknown clipboard error');
    errorCallback?.(error, text);
    return false;
  }
}
